import React, { useEffect, useState } from 'react';

/* ============================================================
   LogicMojo AI Community — full-width CTA card
   Sits just above the syllabus tabs on the home page and links to
   the AI Community page. All classes are scoped under .lm-aic*.
   Layout: 60% content / 40% "visual proof" (heatmap, avatar stack,
   floating project tile, activity ticker). Stacks on mobile, where
   only the avatar stack + ticker remain.
   Everything editable (copy, stats, builders, project, ticker)
   lives in the constants below.
   ============================================================ */

const COMMUNITY_URL = 'https://logicmojo.com/logicmojo-ai-community/';
const COMMUNITY_GITHUB_URL = `${COMMUNITY_URL}#github`;

const BUILDER_IMG_BASE =
  'https://logicmojo.com/assets/dist/new_index_design/images/GithubImages/';

/* Featured learners from the community page (same photos it uses) */
const BUILDERS = [
  { name: 'Rishabh Gupta', img: 'rishab.jpg' },
  { name: 'Sourav Karmakar', img: 'Sourav.jpg' },
  { name: 'Anitha Mani', img: 'Anitha.jpg' },
  { name: 'Surya Anirudh', img: 'surya.jpg' },
  { name: 'Abhinav Bansal', img: 'abhinav.jpg' },
  { name: 'Aishwarya', img: 'aishwarya.jpg' },
];
const BUILDER_COUNT = '+1,200';

const STATS = [
  { value: '1,200+', label: 'active builders', icon: 'live' },
  { value: '500+', label: 'shipped projects', icon: 'box' },
  { value: '8,400+', label: 'GitHub commits', icon: 'bolt' },
];

const PROJECT = {
  name: 'RAG-powered Doc Search',
  tag: 'Agentic AI',
  builder: 'Sourav K.',
  img: 'Sourav.jpg',
  stars: 13,
};

/* Sample lines for the activity ticker (static, not a live feed) */
const ACTIVITY = [
  { handle: '@arjun', text: 'pushed 4 commits', time: '2m ago' },
  { handle: '@priya', text: 'opened 2 pull requests', time: '9m ago' },
  { handle: '@rohit', text: 'merged a RAG fix', time: '21m ago' },
];

/* Contribution heatmap: 7 rows × 24 weeks, fixed pattern, busier towards the right */
const HEAT_ROWS = 7;
const HEAT_COLS = 24;
const HEAT_CELLS = Array.from({ length: HEAT_ROWS * HEAT_COLS }, (_, i) => {
  const col = Math.floor(i / HEAT_ROWS);
  const noise = Math.abs(Math.sin((i + 1) * 12.9898) * 43758.5453) % 1;
  const score = noise * 0.72 + (col / HEAT_COLS) * 0.38;
  if (score > 0.9) return 4;
  if (score > 0.72) return 3;
  if (score > 0.54) return 2;
  if (score > 0.38) return 1;
  return 0;
});

const initials = (name) =>
  name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

const hideBrokenImage = (e) => {
  e.currentTarget.style.display = 'none';
};

const StatIcon = ({ type }) => {
  if (type === 'live') return <span className="lm-aic__livedot" aria-hidden="true" />;
  if (type === 'box') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M21 8 12 3 3 8v8l9 5 9-5V8z" />
        <path d="m3 8 9 5 9-5M12 13v8" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M13 2 4 14h6l-1 8 9-12h-6l1-8z" />
    </svg>
  );
};

const GitHubMark = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 .5a11.5 11.5 0 0 0-3.64 22.41c.58.1.79-.25.79-.56v-2c-3.2.7-3.88-1.36-3.88-1.36-.52-1.33-1.28-1.69-1.28-1.69-1.05-.71.08-.7.08-.7 1.16.08 1.77 1.19 1.77 1.19 1.03 1.77 2.7 1.26 3.36.96.1-.75.4-1.26.73-1.55-2.55-.29-5.24-1.28-5.24-5.68 0-1.26.45-2.28 1.19-3.09-.12-.29-.52-1.46.11-3.05 0 0 .97-.31 3.16 1.18a10.9 10.9 0 0 1 5.76 0c2.19-1.49 3.16-1.18 3.16-1.18.63 1.59.23 2.76.11 3.05.74.81 1.19 1.83 1.19 3.09 0 4.41-2.69 5.39-5.25 5.67.41.36.78 1.06.78 2.14v3.17c0 .31.21.67.8.56A11.5 11.5 0 0 0 12 .5z" />
  </svg>
);

const AICommunityCard = () => {
  const [activityIndex, setActivityIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(
      () => setActivityIndex((i) => (i + 1) % ACTIVITY.length),
      4200
    );
    return () => clearInterval(timer);
  }, []);

  // Whole card is a click target; real links inside keep their own behaviour
  const handleCardClick = (e) => {
    if (e.target.closest('a')) return;
    window.open(COMMUNITY_URL, '_blank', 'noopener,noreferrer');
  };

  const activity = ACTIVITY[activityIndex];

  return (
    <div className="lm-aic-wrap">
      <section
        className="lm-aic"
        aria-labelledby="lm-aic-title"
        onClick={handleCardClick}
      >
        <div className="lm-aic__bg" aria-hidden="true" />

        <div className="lm-aic__inner">
          <div className="lm-aic__content">
            <h2 className="lm-aic__title" id="lm-aic-title">
              LogicMojo <span>AI Community</span>
            </h2>
            <p className="lm-aic__sub">
              Where real learners ship real AI projects — reviewed by working
              engineers.
            </p>
            <p className="lm-aic__desc">
              Explore student profiles, GitHub repositories, and live
              AI/ML/GenAI/Agentic AI projects built by the LogicMojo community.
              Every project is peer-reviewed and portfolio-ready.
            </p>

            <ul className="lm-aic__stats">
              {STATS.map((stat) => (
                <li key={stat.label}>
                  <StatIcon type={stat.icon} />
                  <b>{stat.value}</b> {stat.label}
                </li>
              ))}
            </ul>

            <div className="lm-aic__actions">
              <a href={COMMUNITY_URL} className="lm-aic__cta" target="_blank" rel="noopener noreferrer">
                Explore the AI Community
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </a>
              <a href={COMMUNITY_GITHUB_URL} className="lm-aic__link" target="_blank" rel="noopener noreferrer">
                <GitHubMark />
                See live GitHub activity
              </a>
            </div>
          </div>

          <div className="lm-aic__visual">
            <div className="lm-aic__heat" aria-hidden="true">
              {HEAT_CELLS.map((level, i) => (
                <i key={i} data-l={level} />
              ))}
            </div>

            <div className="lm-aic__builders">
              {BUILDERS.map((builder) => (
                <span className="lm-aic__av" key={builder.name}>
                  {initials(builder.name)}
                  <img
                    src={`${BUILDER_IMG_BASE}${builder.img}`}
                    alt={builder.name}
                    loading="lazy"
                    onError={hideBrokenImage}
                  />
                  <span className="lm-aic__tip">{builder.name}</span>
                </span>
              ))}
              <span className="lm-aic__count">{BUILDER_COUNT}</span>
            </div>

            <div className="lm-aic__project">
              <div className="lm-aic__project-head">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M4 19.5V5a2 2 0 0 1 2-2h14v14H6.5A2.5 2.5 0 0 0 4 19.5zm0 0A2.5 2.5 0 0 0 6.5 22H20v-5" />
                </svg>
                <span>{PROJECT.name}</span>
              </div>
              <span className="lm-aic__tag">{PROJECT.tag}</span>
              <div className="lm-aic__project-foot">
                <span className="lm-aic__av lm-aic__av--sm">
                  {initials(PROJECT.builder)}
                  <img
                    src={`${BUILDER_IMG_BASE}${PROJECT.img}`}
                    alt=""
                    loading="lazy"
                    onError={hideBrokenImage}
                  />
                </span>
                <span className="lm-aic__project-by">{PROJECT.builder}</span>
                <span className="lm-aic__stars">
                  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="m12 2.5 2.95 6.2 6.8.9-4.98 4.72 1.25 6.74L12 17.8l-6.02 3.26 1.25-6.74L2.25 9.6l6.8-.9L12 2.5z" />
                  </svg>
                  {PROJECT.stars}
                </span>
              </div>
            </div>

            <div className="lm-aic__ticker" aria-hidden="true">
              <span className="lm-aic__livedot lm-aic__livedot--pulse" />
              <span className="lm-aic__ticker-text" key={activityIndex}>
                <b>{activity.handle}</b> {activity.text} · {activity.time}
              </span>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .lm-aic-wrap{float:left;width:100%;box-sizing:border-box;padding:48px 15px 0;font-family:'Inter','Geist',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;}
        .lm-aic{position:relative;isolation:isolate;border-radius:22px;color:#fff;cursor:pointer;text-align:left;box-shadow:0 20px 44px -24px rgba(24,16,84,.7),0 2px 8px rgba(15,23,42,.12);transition:transform .35s cubic-bezier(.2,.7,.2,1),box-shadow .35s ease;}
        .lm-aic:hover{transform:translateY(-4px);box-shadow:0 36px 68px -28px rgba(48,28,140,.8),0 8px 20px rgba(15,23,42,.18);}
        .lm-aic::after{content:"";position:absolute;inset:0;border-radius:inherit;border:1px solid rgba(255,255,255,.1);pointer-events:none;}

        /* background: navy → violet, cyan glow in the corners, grain on top */
        .lm-aic__bg{position:absolute;inset:0;z-index:-1;border-radius:inherit;overflow:hidden;background:radial-gradient(520px 280px at 0% 0%,rgba(34,211,238,.2),transparent 62%),radial-gradient(560px 320px at 100% 100%,rgba(34,211,238,.24),transparent 64%),linear-gradient(118deg,#070b24 0%,#141046 44%,#3b1a86 100%);}
        .lm-aic__bg::before{content:"";position:absolute;inset:0;background:radial-gradient(620px 320px at 14% 0%,rgba(34,211,238,.26),transparent 62%),radial-gradient(560px 320px at 100% 100%,rgba(94,234,212,.22),transparent 64%),linear-gradient(118deg,#0a0f33 0%,#24157a 46%,#5b21b6 100%);opacity:0;transition:opacity .5s ease;}
        .lm-aic:hover .lm-aic__bg::before{opacity:1;}
        .lm-aic__bg::after{content:"";position:absolute;inset:0;background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.6'/%3E%3C/svg%3E");opacity:.2;mix-blend-mode:overlay;}

        .lm-aic__inner{position:relative;display:grid;grid-template-columns:minmax(0,3fr) minmax(0,2fr);gap:36px;align-items:center;box-sizing:border-box;min-height:290px;padding:32px 38px;}

        /* ---- left: content ---- */
        .lm-aic__content{position:relative;isolation:isolate;min-width:0;}
        .lm-aic__content::before{content:"";position:absolute;z-index:-1;left:-60px;top:-40px;width:460px;height:340px;background:radial-gradient(closest-side,rgba(124,92,255,.34),transparent);pointer-events:none;}
        .lm-aic .lm-aic__title{margin:0 0 8px;padding:0;font-family:inherit;font-size:34px;line-height:1.12;font-weight:700;letter-spacing:-.03em;color:#fff;text-transform:none;}
        .lm-aic .lm-aic__title span{background:linear-gradient(90deg,#fff 0%,#c4b5fd 55%,#67e8f9 100%);-webkit-background-clip:text;background-clip:text;-webkit-text-fill-color:transparent;}
        .lm-aic .lm-aic__sub{margin:0 0 8px;padding:0;font-size:15.5px;line-height:1.45;font-weight:500;color:rgba(226,232,255,.82);}
        .lm-aic .lm-aic__desc{margin:0 0 14px;padding:0;max-width:600px;font-size:13.5px;line-height:1.6;font-weight:400;color:rgba(203,213,245,.7);}
        .lm-aic .lm-aic__stats{display:flex;flex-wrap:wrap;align-items:center;gap:6px 0;margin:0 0 18px;padding:0;list-style:none;}
        .lm-aic .lm-aic__stats li{display:inline-flex;align-items:center;gap:6px;margin:0;padding:0;list-style:none;font-size:13px;line-height:1.4;font-weight:500;color:rgba(214,222,250,.78);white-space:nowrap;}
        .lm-aic .lm-aic__stats li:not(:last-child)::after{content:"";width:3px;height:3px;margin:0 12px 0 6px;border-radius:50%;background:rgba(255,255,255,.38);}
        .lm-aic .lm-aic__stats b{font-weight:700;color:#fff;}
        .lm-aic__stats svg{width:14px;height:14px;color:#a5b4fc;flex-shrink:0;}
        .lm-aic__livedot{position:relative;flex-shrink:0;width:8px;height:8px;border-radius:50%;background:#3fd160;box-shadow:0 0 8px rgba(63,209,96,.8);}
        .lm-aic__livedot--pulse::after{content:"";position:absolute;inset:0;border-radius:50%;background:#3fd160;animation:lm-aic-pulse 2s ease-out infinite;}

        .lm-aic__actions{display:flex;flex-wrap:wrap;align-items:center;gap:12px 22px;}
        .lm-aic .lm-aic__cta{display:inline-flex;align-items:center;justify-content:center;gap:10px;min-height:48px;box-sizing:border-box;padding:0 24px;border-radius:14px;font-size:15px;line-height:1.2;font-weight:700;letter-spacing:-.005em;color:#fff;text-decoration:none;background-image:linear-gradient(90deg,#4f46e5 0%,#7c3aed 25%,#2f6bff 50%,#7c3aed 75%,#4f46e5 100%);background-size:200% 100%;background-position:0% 0;box-shadow:0 10px 26px -8px rgba(124,92,255,.8),0 0 18px rgba(34,211,238,.14),inset 0 0 0 1px rgba(255,255,255,.18);transition:transform .25s ease,box-shadow .35s ease,background-position .6s ease;}
        .lm-aic:hover .lm-aic__cta{box-shadow:0 14px 34px -6px rgba(124,92,255,1),0 0 30px rgba(34,211,238,.38),inset 0 0 0 1px rgba(255,255,255,.26);}
        .lm-aic .lm-aic__cta:hover{transform:scale(1.03);background-position:100% 0;color:#fff;text-decoration:none;}
        .lm-aic__cta svg{width:16px;height:16px;transition:transform .25s ease;}
        .lm-aic__cta:hover svg{transform:translateX(4px);}
        .lm-aic .lm-aic__link{display:inline-flex;align-items:center;gap:7px;font-size:13.5px;line-height:1.2;font-weight:600;color:rgba(226,232,255,.82);text-decoration:none;border-bottom:1px solid rgba(226,232,255,.28);padding-bottom:2px;transition:color .2s ease,border-color .2s ease;}
        .lm-aic .lm-aic__link:hover{color:#7ee2a0;border-color:#7ee2a0;text-decoration:none;}
        .lm-aic__link svg{width:14px;height:14px;}
        .lm-aic__cta:focus-visible,.lm-aic__link:focus-visible{outline:2px solid #67e8f9;outline-offset:3px;}

        /* ---- right: visual proof ---- */
        .lm-aic__visual{position:relative;min-width:0;height:226px;}
        .lm-aic__heat{position:absolute;top:0;right:0;display:grid;grid-template-rows:repeat(7,11px);grid-auto-flow:column;grid-auto-columns:11px;gap:3px;-webkit-mask-image:linear-gradient(255deg,#000 30%,transparent 96%);mask-image:linear-gradient(255deg,#000 30%,transparent 96%);}
        .lm-aic__heat i{display:block;border-radius:3px;background:rgba(255,255,255,.07);}
        .lm-aic__heat i[data-l="1"]{background:rgba(46,160,67,.38);}
        .lm-aic__heat i[data-l="2"]{background:rgba(46,160,67,.78);}
        .lm-aic__heat i[data-l="3"]{background:#3fd160;box-shadow:0 0 6px rgba(63,209,96,.55);}
        .lm-aic__heat i[data-l="4"]{background:#5df2c0;box-shadow:0 0 9px rgba(94,242,192,.8);}
        /* shimmer pass, masked to the cell grid so it only lights the squares */
        .lm-aic__heat::after{content:"";position:absolute;inset:0;pointer-events:none;background:linear-gradient(105deg,transparent 42%,rgba(255,255,255,.6) 50%,transparent 58%) no-repeat;background-size:250% 100%;background-position:100% 0;-webkit-mask-image:repeating-linear-gradient(90deg,#000 0 11px,transparent 11px 14px),repeating-linear-gradient(180deg,#000 0 11px,transparent 11px 14px);-webkit-mask-composite:source-in;mask-image:repeating-linear-gradient(90deg,#000 0 11px,transparent 11px 14px),repeating-linear-gradient(180deg,#000 0 11px,transparent 11px 14px);mask-composite:intersect;animation:lm-aic-shimmer 6s ease-in-out infinite;}

        .lm-aic__builders{position:absolute;left:0;top:30px;z-index:3;display:inline-flex;align-items:center;padding:7px 10px 7px 7px;border-radius:999px;background:rgba(255,255,255,.09);border:1px solid rgba(255,255,255,.18);-webkit-backdrop-filter:blur(12px) saturate(150%);backdrop-filter:blur(12px) saturate(150%);box-shadow:0 14px 30px -16px rgba(0,0,0,.7);}
        .lm-aic__av{position:relative;flex-shrink:0;display:grid;place-items:center;width:40px;height:40px;margin-left:-12px;border-radius:50%;border:2px solid #1d1757;background:linear-gradient(135deg,#6d5cff,#22d3ee);font-size:12.5px;font-weight:700;color:#fff;transition:margin .3s ease,transform .3s ease;}
        .lm-aic__av:first-child{margin-left:0;}
        .lm-aic .lm-aic__av img{position:absolute;inset:0;width:100%;height:100%;max-width:none;margin:0;border-radius:50%;object-fit:cover;}
        .lm-aic__tip{position:absolute;left:50%;bottom:calc(100% + 9px);z-index:5;padding:4px 9px;border-radius:8px;background:#0b1026;border:1px solid rgba(255,255,255,.18);font-size:11.5px;line-height:1.3;font-weight:600;color:#fff;white-space:nowrap;opacity:0;transform:translate(-50%,4px);pointer-events:none;transition:opacity .2s ease,transform .2s ease;}
        .lm-aic__count{margin-left:8px;padding:5px 10px;border-radius:999px;background:rgba(63,209,96,.14);border:1px solid rgba(63,209,96,.42);font-size:12px;line-height:1.2;font-weight:700;color:#7ee2a0;white-space:nowrap;}
        @media(hover:hover){
          .lm-aic__builders:hover .lm-aic__av{margin-left:-3px;}
          .lm-aic__builders:hover .lm-aic__av:first-child{margin-left:0;}
          .lm-aic__av:hover{z-index:4;transform:translateY(-3px) scale(1.08);}
          .lm-aic__av:hover .lm-aic__tip{opacity:1;transform:translate(-50%,0);}
        }

        .lm-aic__project{position:absolute;right:0;top:82px;z-index:2;width:232px;box-sizing:border-box;padding:13px 14px 12px;border-radius:14px;background:rgba(255,255,255,.1);border:1px solid rgba(255,255,255,.2);-webkit-backdrop-filter:blur(14px) saturate(150%);backdrop-filter:blur(14px) saturate(150%);box-shadow:0 20px 38px -18px rgba(0,0,0,.75);transform:rotate(-2deg);animation:lm-aic-float 6s ease-in-out infinite;}
        .lm-aic__project-head{display:flex;align-items:center;gap:7px;margin-bottom:8px;font-size:13.5px;line-height:1.3;font-weight:700;color:#fff;}
        .lm-aic__project-head svg{flex-shrink:0;width:14px;height:14px;color:#a5b4fc;}
        .lm-aic__project-head span{overflow:hidden;white-space:nowrap;text-overflow:ellipsis;}
        .lm-aic__tag{display:inline-block;padding:3px 9px;border-radius:999px;background:rgba(124,92,255,.28);border:1px solid rgba(165,180,252,.45);font-size:11px;line-height:1.3;font-weight:600;color:#ddd6fe;}
        .lm-aic__project-foot{display:flex;align-items:center;gap:7px;margin-top:10px;font-size:12px;line-height:1.2;color:rgba(226,232,255,.78);}
        .lm-aic__av.lm-aic__av--sm{width:22px;height:22px;margin-left:0;border-width:1px;font-size:9px;}
        .lm-aic__project-by{font-weight:500;}
        .lm-aic__stars{display:inline-flex;align-items:center;gap:4px;margin-left:auto;font-weight:700;color:#fff;}
        .lm-aic__stars svg{width:13px;height:13px;color:#fbbf24;}

        .lm-aic__ticker{position:absolute;left:0;bottom:0;z-index:3;display:inline-flex;align-items:center;gap:9px;max-width:100%;box-sizing:border-box;padding:7px 13px 7px 11px;border-radius:999px;background:rgba(4,8,28,.55);border:1px solid rgba(255,255,255,.12);font-size:12px;line-height:1.3;color:rgba(214,222,250,.8);}
        .lm-aic__ticker-text{overflow:hidden;white-space:nowrap;text-overflow:ellipsis;animation:lm-aic-tick .5s ease both;}
        .lm-aic__ticker-text b{font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;font-size:11.5px;font-weight:600;color:#7ee2a0;}

        @keyframes lm-aic-pulse{0%{transform:scale(1);opacity:.7;}100%{transform:scale(3.2);opacity:0;}}
        @keyframes lm-aic-shimmer{0%,68%{background-position:100% 0;}100%{background-position:0% 0;}}
        @keyframes lm-aic-float{0%,100%{transform:rotate(-2deg) translateY(0);}50%{transform:rotate(-2deg) translateY(-5px);}}
        @keyframes lm-aic-tick{from{opacity:0;transform:translateY(5px);}to{opacity:1;transform:translateY(0);}}

        /* tablet: keep two columns, shrink the visual block */
        @media(max-width:991px){
          .lm-aic__inner{gap:24px;padding:28px 26px;}
          .lm-aic .lm-aic__title{font-size:28px;}
          .lm-aic .lm-aic__sub{font-size:14.5px;}
          .lm-aic__av{width:32px;height:32px;margin-left:-10px;font-size:11px;}
          .lm-aic__project{width:218px;padding:12px 12px 11px;}
          .lm-aic__project-head{font-size:13px;}
          .lm-aic .lm-aic__stats{gap:6px 18px;}
          .lm-aic .lm-aic__stats li:not(:last-child)::after{display:none;}
          .lm-aic__heat{width:221px;overflow:hidden;justify-content:end;}
        }
        /* mobile: stack; visual block becomes an avatar + ticker strip */
        @media(max-width:767px){
          .lm-aic-wrap{padding:28px 0 6px;}
          .lm-aic{border-radius:20px;}
          .lm-aic__inner{grid-template-columns:minmax(0,1fr);gap:20px;min-height:0;padding:26px 20px 22px;}
          .lm-aic .lm-aic__title{font-size:26px;}
          .lm-aic__actions{flex-direction:column;align-items:stretch;}
          .lm-aic .lm-aic__link{align-self:center;}
          .lm-aic__heat,.lm-aic__project{display:none;}
          .lm-aic__visual{display:flex;flex-wrap:wrap;align-items:center;gap:10px 12px;height:auto;padding-top:18px;border-top:1px solid rgba(255,255,255,.12);}
          .lm-aic__builders,.lm-aic__ticker{position:relative;top:auto;bottom:auto;}
        }
        @media(prefers-reduced-motion:reduce){
          .lm-aic,.lm-aic .lm-aic__cta,.lm-aic__cta svg,.lm-aic__av{transition:none;}
          .lm-aic:hover,.lm-aic .lm-aic__cta:hover{transform:none;}
          .lm-aic__heat::after,.lm-aic__project,.lm-aic__livedot--pulse::after,.lm-aic__ticker-text{animation:none;}
        }
      `}</style>
    </div>
  );
};

export default AICommunityCard;
